import { Badge, Button } from "@procurement-agents/ui";
import { ApiError, type EditableSection, type QAFinding, type SectionDraft } from "@procurement-agents/scope-api";
import { useEffect, useRef, useState } from "react";
import { ruleText } from "../risk";

/** Inline editor for a section's model-generated text. Fixed parts are shown but locked (SPEC_SLICE4 section 3). */
export function SectionEditor({ sectionId, load, save, onSaved, onCancel, focus }: { sectionId: string; load: () => Promise<EditableSection>; save: (content: string, confirmTableChange?: boolean) => Promise<{ section: SectionDraft; findings: QAFinding[] }>; onSaved: (section: SectionDraft, findings: QAFinding[]) => void; onCancel: () => void; focus?: string | null }) {
  const [editable, setEditable] = useState<EditableSection | null>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [findings, setFindings] = useState<QAFinding[] | null>(null);
  const [tableChange, setTableChange] = useState<string | null>(null);   // B4: a change to a table's rows or columns is kept only on purpose
  useEffect(() => { load().then((e) => { setEditable(e); setText(e.content); }).catch((e) => setError(e instanceof ApiError ? e.detail : String(e))); }, [load]);
  // CHANGE_review_responses: opened from a finding ("I'll edit"), the clause QA quoted is selected and in view
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!editable || !focus || !box.current) return;
    const at = editable.content.indexOf(focus.replace(/^["“]|["”]$/g, ""));
    box.current.focus();
    if (at >= 0) box.current.setSelectionRange(at, at + focus.replace(/^["“]|["”]$/g, "").length);
  }, [editable, focus]);
  if (error && !editable) return <p className="error">{error}</p>;
  if (!editable) return <p className="muted small">Loading…</p>;
  const { locked } = editable;
  const submit = async (confirmTableChange = false) => {
    setBusy(true); setError(null); setTableChange(null);
    try {
      const out = await (confirmTableChange ? save(text, true) : save(text));
      setFindings(out.findings);
      onSaved(out.section, out.findings);
    } catch (e) {
      if (e instanceof ApiError && e.code === "table_changed") setTableChange(e.detail);
      else setError(e instanceof ApiError ? e.detail : String(e));
    }
    finally { setBusy(false); }
  };
  return (
    <div className="editor" data-testid={`editor-${sectionId}`}>
      {locked.mandatory_text && locked.mandatory_placement === "prefix" && <div className="fixed-text locked"><span className="small muted">Fixed text (policy)</span>{locked.mandatory_text}</div>}
      {locked.reference_text && locked.reference_placement === "prefix" && <div className="fixed-text locked"><span className="small muted">Reference table (fixed)</span><pre>{locked.reference_text}</pre></div>}
      <textarea ref={box} value={text} onChange={(e) => setText(e.target.value)} rows={Math.min(30, Math.max(8, text.split("\n").length + 2))} aria-label={`Edit ${editable.title}`} />
      {locked.reference_text && locked.reference_placement === "suffix" && <div className="fixed-text locked"><span className="small muted">Reference table (fixed)</span><pre>{locked.reference_text}</pre></div>}
      {locked.mandatory_text && locked.mandatory_placement === "suffix" && <div className="fixed-text locked"><span className="small muted">Fixed text (policy)</span>{locked.mandatory_text}</div>}
      {error && <p className="error">{error}</p>}
      {tableChange && <p className="row wrap warn-text small" role="alert">{tableChange} <Button variant="primary" disabled={busy} onClick={() => void submit(true)}>Keep the change</Button><Button variant="quiet" onClick={() => setTableChange(null)}>Go back</Button></p>}
      {findings && (
        <ul className="findings inline">
          {findings.length === 0 && <li className="muted">No deterministic findings.</li>}
          {findings.map((f, i) => <li key={i}><Badge tone={f.severity === "blocker" ? "bad" : f.severity === "major" ? "warn" : "neutral"}>{f.severity}</Badge> <strong>{ruleText(f.rule)}</strong>: {f.description} <span className="muted small">{f.evidence}</span></li>)}
        </ul>
      )}
      <div className="row">
        <Button variant="primary" onClick={() => void submit()} disabled={busy || text.trim() === editable.content.trim() || !text.trim()}>Save</Button>
        <Button variant="quiet" onClick={onCancel} disabled={busy}>{findings ? "Done" : "Cancel"}</Button>
        <span className="muted small">Markdown as written; the fixed parts stay as they are.</span>
      </div>
    </div>
  );
}
